import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { encodeScores, encodeTops, parseScript, pickTop } from "./parseScript";
import { StorySound } from "./sound";
import { PixelHeart } from "./PixelHeart";
import { tx } from "./texts";
import { PixelStage, usePixelFont } from "./pixel/PixelStage";
import {
  LIGHT_MOODS,
  MOODS,
  blink,
  drift,
  fadeIn,
  fadeUp,
  float,
  noMotion,
  pulse,
  rain,
  shake,
  slideDown,
} from "./effects";

const SPEED = { mono: 42, speech: 36, inner: 64 }; // 글자당 ms
const PAUSE_MS = 1100; // [쉼]
const BLACKOUT_MS = 1400; // [암전]
const ENDING_MS = 2800; // 결과로 넘어가기 전 정리 화면
const LOG_LIMIT = 7; // 화면에 남겨두는 지난 줄 수
const HINT_MS = 5200; // '공감' 안내 말풍선이 떠 있는 시간

const isDev = process.env.NODE_ENV === "development";
const reduceMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export const StoryPlayer = ({ test }) => {
  const navigate = useNavigate();
  const parsed = useMemo(() => parseScript(test.script), [test.script]);
  const { steps, traits } = parsed;
  const sound = useRef(null);
  if (!sound.current) sound.current = new StorySound(test.sounds, { chip: test.look === "pixel" });

  // 진행 상태
  const pos = useRef(0); // 다음에 읽을 steps 위치
  const extra = useRef([]); // 선택 후 끼워 넣을 반응 줄
  const scores = useRef({});
  const recency = useRef({}); // 습관 → 마지막으로 점수를 얻은 순번 (동점 처리용)
  const pickCount = useRef(0);
  const uid = useRef(0);
  const busy = useRef(false); // 쉼·암전 중에는 탭 무시
  const alive = useRef(true); // 화면을 떠난 뒤 예약된 동작 막기

  const [started, setStarted] = useState(false);
  const [log, setLog] = useState([]); // 화면에 쌓인 줄
  const [current, setCurrentState] = useState(null); // 지금 타이핑 중인 줄 / 선택 / 장 카드
  const currentRef = useRef(null);
  const setCurrent = useCallback((item) => {
    currentRef.current = item;
    setCurrentState(item);
  }, []);
  const [typed, setTyped] = useState(0);
  const [mood, setMood] = useState({ now: "기본", prev: null });
  const [music, setMusic] = useState(null);
  const [muted, setMuted] = useState(sound.current.muted);
  const [fx, setFx] = useState({ shake: 0, pulse: 0 });
  const [blackout, setBlackout] = useState(false);
  const [ending, setEnding] = useState(false);
  const [progress, setProgress] = useState(0);
  const [devOpen, setDevOpen] = useState(false);
  // 읽기 지도: 지금 읽는 조각과 '공감' 표시
  const [part, setPart] = useState(null);
  const [hearts, setHearts] = useState({});
  const [heartPop, setHeartPop] = useState(0);
  const heartsRef = useRef({});
  const [hint, setHint] = useState(false); // '공감' 첫 안내 (한 번만)
  const hintShown = useRef(false);
  // 픽셀 모드 (test.look === "pixel"): 위쪽 장면과 장면 속 연출
  const pixel = test.look === "pixel";
  usePixelFont(pixel);
  const [scene, setScene] = useState(null);
  const [cues, setCues] = useState([]);

  useEffect(() => {
    const s = sound.current;
    alive.current = true;
    return () => {
      alive.current = false;
      s.dispose();
    };
  }, []);

  const lineLength = current && current.type === "line" ? current.text.length : 0;
  const typing = current?.type === "line" && typed < lineLength;

  /* ---------- 연출 지시 적용 ---------- */
  const applyDirect = useCallback((step) => {
    if (step.part !== undefined) setPart(step.part || null);
    if (step.part && test.inlineHearts && !hintShown.current) {
      hintShown.current = true;
      setTimeout(() => alive.current && setHint(true), 900);
      setTimeout(() => alive.current && setHint(false), 900 + HINT_MS);
    }
    if (step.scene) {
      setScene(step.scene);
      setCues([]);
    }
    if (step.cues) {
      const at = performance.now();
      setCues((c) => [...c, ...step.cues.map((name) => ({ name, at }))]);
    }
    if (step.mood) {
      setMood((m) => (m.now === step.mood ? m : { now: step.mood, prev: m.now }));
    }
    if (step.music) {
      setMusic(step.music);
      sound.current.music(step.music);
    }
    let wait = 0;
    step.effects.forEach((e) => {
      if (e === "흔들림") setFx((f) => ({ ...f, shake: f.shake + 1 }));
      if (e === "두근") {
        setFx((f) => ({ ...f, pulse: f.pulse + 1 }));
        sound.current.heartbeat();
        if (!reduceMotion()) navigator.vibrate?.([50, 120, 50, 520, 50, 120, 50]);
      }
      if (e === "쉼") wait = Math.max(wait, PAUSE_MS);
      if (e === "암전") {
        setBlackout(true);
        setTimeout(() => setBlackout(false), BLACKOUT_MS);
        wait = Math.max(wait, BLACKOUT_MS);
      }
    });
    return wait;
  }, [test.inlineHearts]);

  /* ---------- 다음 단계로 ---------- */
  const finish = useCallback(() => {
    setCurrent(null);
    setEnding(true);
    sound.current.music("끔");
    const groups = parsed.groups.length ? parsed.groups : [{ traits }];
    const tops = groups.map((g) => pickTop(scores.current, g.traits, recency.current));
    const top = tops[0];
    const result =
      test.result.find((r) => r.trait === top) || test.result[0];
    const key = result.key ?? result.count;
    let s =
      encodeScores(scores.current, traits) +
      (parsed.groups.length ? `&t=${encodeTops(tops, parsed.groups)}` : "");
    if (test.resultMode === "reading" && test.parts && test.inlineHearts) {
      // 공감한 조각 → 읽기 지도에 미리 표시 (고정 조각은 제외)
      s += `&r=${test.parts.filter((p) => !p.fixed).map((p) => (heartsRef.current[p.key] ? 1 : 0)).join("")}`;
    }
    setTimeout(() => {
      if (alive.current) {
        navigate(`/main-test/${test.id}/result/${encodeURIComponent(key)}?s=${s}`);
      }
    }, ENDING_MS);
  }, [navigate, parsed, setCurrent, test, traits]);

  const advance = useCallback(() => {
    if (busy.current) return;
    // 지금 줄을 로그로 넘김
    const cur = currentRef.current;
    if (cur && (cur.type === "line" || cur.type === "notify")) {
      setLog((l) => [...l, cur].slice(-LOG_LIMIT));
    }
    setCurrent(null);

    // 연출 지시는 탭 없이 연달아 적용
    for (;;) {
      const next = extra.current.length ? extra.current.shift() : steps[pos.current++];
      setProgress(Math.min(1, pos.current / steps.length));
      if (!next) {
        finish();
        return;
      }
      if (next.type === "direct") {
        const wait = applyDirect(next);
        if (wait) {
          busy.current = true;
          setTimeout(() => {
            busy.current = false;
            if (alive.current) advance();
          }, wait);
          return;
        }
        continue;
      }
      const item = { ...next, id: ++uid.current };
      if (next.type === "chapter") {
        setLog([]);
        sound.current.whoosh();
      }
      if (next.type === "notify") sound.current.notify();
      setTyped(0);
      setCurrent(item);
      return;
    }
  }, [applyDirect, finish, setCurrent, steps]);

  /* ---------- 타자기 ---------- */
  useEffect(() => {
    if (!typing) return;
    const t = setTimeout(() => {
      setTyped((n) => n + 1);
      if (current.style !== "inner" && typed % 2 === 0) sound.current.tick();
    }, SPEED[current.style] || SPEED.mono);
    return () => clearTimeout(t);
  }, [typing, typed, current]);

  /* ---------- 입력 ---------- */
  const begin = () => {
    sound.current.start();
    setStarted(true);
    advance();
  };

  const onTap = () => {
    if (!started) return begin();
    if (ending || !current || current.type === "choice") return;
    if (typing) {
      setTyped(lineLength);
      return;
    }
    advance();
  };

  const choose = (option) => {
    const order = ++pickCount.current;
    Object.entries(option.scores).forEach(([k, v]) => {
      scores.current[k] = (scores.current[k] || 0) + v;
      recency.current[k] = order;
    });
    sound.current.select();
    setLog((l) =>
      [...l, { type: "picked", text: option.text, id: ++uid.current }].slice(-LOG_LIMIT)
    );
    extra.current = [...option.reaction, ...extra.current];
    setCurrent(null);
    advance();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.closest?.("button")) return;
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        onTap();
      } else if (current?.type === "choice" && /^[1-9]$/.test(e.key)) {
        const opt = current.options[Number(e.key) - 1];
        if (opt) choose(opt);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const toggleHeart = (e) => {
    e.stopPropagation();
    if (!part) return;
    const next = { ...heartsRef.current, [part]: !heartsRef.current[part] };
    heartsRef.current = next;
    setHearts(next);
    setHeartPop((n) => n + 1);
    setHint(false);
    if (next[part]) sound.current.select();
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    sound.current.setMuted(!muted);
    setMuted(!muted);
  };

  const jumpTo = (index) => {
    // 건너뛴 구간의 마지막 장면·조각을 되살림
    let sc = null, pt;
    for (let i = index - 1; i >= 0; i--) {
      const st = steps[i];
      if (st.type !== "direct") continue;
      if (!sc && st.scene) sc = st.scene;
      if (pt === undefined && st.part !== undefined) pt = st.part || null;
      if (sc && pt !== undefined) break;
    }
    setScene(sc);
    setCues([]);
    setPart(pt ?? null);
    extra.current = [];
    pos.current = index;
    setLog([]);
    setCurrent(null);
    setDevOpen(false);
    if (!started) {
      sound.current.start();
      setStarted(true);
    }
    advance();
  };

  const light = LIGHT_MOODS.includes(mood.now);
  const raining = mood.now === "비" || music === "빗소리";

  // 읽는 중 '공감' 하트는 test.inlineHearts 일 때만 (기본: 이야기가 끝난 뒤 한 번에 묻기)
  const heartButton = test.inlineHearts && started && part && !ending && current?.type !== "chapter" && (
    <>
    {hint && (
      <HeartHint $pixel={pixel} role="status" onClick={(e) => { e.stopPropagation(); setHint(false); }}>
        {test.heartHint || "읽다가 '내 얘기 같다' 싶은 순간,\n♥ 공감을 눌러 주세요. 다시 누르면 취소돼요."}
      </HeartHint>
    )}
    <HeartBtn
      key={part}
      type="button"
      $light={light && !pixel}
      $pixel={pixel}
      aria-pressed={!!hearts[part]}
      aria-label={hearts[part] ? "공감 취소" : "공감"}
      onClick={toggleHeart}
    >
      <PixelHeart on={!!hearts[part]} pop={heartPop} size={30} />
      <span>공감</span>
    </HeartBtn>
    </>
  );


  return (
    <Screen
      className={pixel ? "pixel" : undefined}
      $pixel={pixel}
      $light={light && !pixel}
      onClick={onTap}
      role="application"
      aria-label={`${test.title} 스토리`}
    >
      {!pixel && mood.prev && <MoodLayer style={{ background: MOODS[mood.prev] || MOODS.기본 }} />}
      {!pixel && (
        <MoodLayer
          key={mood.now}
          $fade={!!mood.prev}
          style={{ background: MOODS[mood.now] || MOODS.기본 }}
        />
      )}
      {!pixel && <Glow $light={light} />}
      {!pixel && raining && <Rain />}
      {!pixel && <Vignette />}
      {fx.pulse > 0 && <Pulse key={`p${fx.pulse}`} />}

      {!pixel && heartButton}

      <Shaker key={`s${fx.shake}`} $shake={fx.shake > 0}>
        <TopBar>
          <IconBtn
            type="button"
            aria-label="나가기"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/test-main/${test.id}`);
            }}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </IconBtn>
          <Progress>
            <span style={{ width: `${progress * 100}%` }} />
          </Progress>
          <IconBtn type="button" aria-label={muted ? "소리 켜기" : "소리 끄기"} onClick={toggleMute}>
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M4 9h4l5-4v14l-5-4H4z" fill="currentColor" />
              {muted ? (
                <path d="M16 9l5 6M21 9l-5 6" stroke="currentColor" strokeWidth="1.8" />
              ) : (
                <path
                  d="M16 8.5a5 5 0 010 7M18.5 6a8.5 8.5 0 010 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                />
              )}
            </svg>
          </IconBtn>
        </TopBar>

        {pixel && started && (
          <PixelStage scene={scene} cues={cues} hidden={current?.type === "chapter"} />
        )}

        {!started && (
          <Opening>
            <OpeningTitle>{test.title}</OpeningTitle>
            <Phones viewBox="0 0 48 48" aria-hidden="true">
              <path
                d="M8 30v-6a16 16 0 0132 0v6M8 30h5v11H8a3 3 0 01-3-3v-5a3 3 0 013-3zm32 0h-5v11h5a3 3 0 003-3v-5a3 3 0 00-3-3z"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </Phones>
            <p>{tx(test, "openingHint")}</p>
            <Hint>{tx(test, "openingTap")}</Hint>
          </Opening>
        )}

        {current?.type === "chapter" && (
          <Chapter key={current.id}>
            <ChapterText>{current.text}</ChapterText>
            <Hint>{tx(test, "chapterTap")}</Hint>
          </Chapter>
        )}

        {started && current?.type !== "chapter" && (
          <Stage $heart={!!heartButton} $pixel={pixel}>
            <Log>
              {(pixel ? log.slice(-1) : log).map((item, i, arr) => (
                <Entry key={item.id} item={item} age={arr.length - i} light={light} />
              ))}
              {current && current.type !== "choice" && current.type !== "notify" && (
                <Entry
                  key={current.id}
                  item={current}
                  age={0}
                  light={light}
                  text={current.type === "line" ? current.text.slice(0, typed) : undefined}
                />
              )}
            </Log>

            {current?.type === "notify" && (
              <TopNotify key={current.id}>
                <Entry item={current} age={0} light={light} />
              </TopNotify>
            )}

            {current?.type === "choice" && (
              <Choices key={current.id} onClick={(e) => e.stopPropagation()}>
                {current.prompt && <Prompt>{current.prompt}</Prompt>}
                {current.variant === "thought" ? (
                  <Bubbles>
                    {current.options.map((opt, i) => (
                      <Bubble
                        key={i}
                        type="button"
                        style={{ animationDelay: `${i * 0.12}s, ${0.6 + i * 0.7}s` }}
                        onClick={() => choose(opt)}
                      >
                        {opt.text}
                      </Bubble>
                    ))}
                  </Bubbles>
                ) : (
                  current.options.map((opt, i) => (
                    <Action
                      key={i}
                      type="button"
                      $light={light}
                      style={{ animationDelay: `${i * 0.1}s` }}
                      onClick={() => choose(opt)}
                    >
                      {opt.text}
                    </Action>
                  ))
                )}
              </Choices>
            )}

            {current && current.type !== "choice" && !typing && <Next aria-hidden="true">▼</Next>}
            {pixel && heartButton}
          </Stage>
        )}
      </Shaker>

      {(blackout || ending) && (
        <Blackout>
          {ending && <EndingText>{test.endingText || "당신의 생각 습관을 정리하고 있어요…"}</EndingText>}
        </Blackout>
      )}

      {false && isDev && started && (
        <DevPanel onClick={(e) => e.stopPropagation()}>
          <button type="button" onClick={() => setDevOpen((o) => !o)}>
            장 이동
          </button>
          {devOpen && (
            <ul>
              {steps.map((s, i) =>
                s.type === "chapter" ? (
                  <li key={i}>
                    <button type="button" onClick={() => jumpTo(i)}>
                      {s.text}
                    </button>
                  </li>
                ) : null
              )}
            </ul>
          )}
        </DevPanel>
      )}
    </Screen>
  );
};

const Entry = ({ item, age, light, text }) => {
  const style = { opacity: age === 0 ? 1 : Math.max(0.18, 0.78 - age * 0.11) };
  if (item.type === "notify") {
    return (
      <Notify style={style}>
        <NotifyIcon aria-hidden="true">
          <svg viewBox="0 0 24 24" width="18" height="18">
            <rect x="3" y="5.5" width="18" height="13" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
            <path d="M3.5 7l8.5 6 8.5-6" fill="none" stroke="currentColor" strokeWidth="1.8" />
          </svg>
        </NotifyIcon>
        <div>
          <strong>{item.from}</strong>
          <span>{item.text}</span>
        </div>
        <small>지금</small>
      </Notify>
    );
  }
  if (item.type === "picked") {
    return (
      <Picked style={style} $light={light}>
        {item.text}
      </Picked>
    );
  }
  return (
    <Line style={style} $style={item.style} $light={light} data-s={item.style}>
      {text ?? item.text}
    </Line>
  );
};

/* ---------- 스타일 ---------- */

const Screen = styled.div`
  position: absolute;
  inset: 0;
  z-index: 9;
  overflow: hidden;
  font-family: "GyeonggiBatang", serif;
  color: ${(p) => (p.$light ? "#22201d" : "#f1ece4")};
  transition: color 1.2s ease;
  cursor: pointer;
  user-select: none;
  ${(p) =>
    p.$pixel &&
    css`
      font-family: "Galmuri11", "Galmuri9", monospace;
      background: #0b0a12;
      color: #fff4dc;
      letter-spacing: 0;
    `}
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
`;

const HeartBtn = styled.button`
  position: absolute;
  right: 18px;
  bottom: calc(22px + env(safe-area-inset-bottom));
  z-index: 6;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 10px 10px 8px;
  border: 0;
  border-radius: 14px;
  background: ${(p) => (p.$light ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.22)")};
  color: ${(p) => (p.$light ? "rgba(34,32,29,0.55)" : "rgba(241,236,228,0.6)")};
  font: inherit;
  font-size: 11px;
  letter-spacing: 0.04em;
  cursor: pointer;
  animation: ${fadeIn} 0.6s ease both;
  ${noMotion}
  ${(p) =>
    p.$pixel &&
    css`
      right: 8px;
      bottom: 8px;
      background: transparent;
      color: rgba(255, 244, 220, 0.55);
    `}
`;

const hintIn = keyframes`
  0%   { opacity: 0; transform: translateY(6px); }
  12%  { opacity: 1; transform: none; }
  88%  { opacity: 1; transform: none; }
  100% { opacity: 0; transform: translateY(-4px); }
`;

const HeartHint = styled.div`
  position: absolute;
  right: 14px;
  bottom: calc(96px + env(safe-area-inset-bottom));
  z-index: 7;
  max-width: 230px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #fff4dc;
  color: #1f2022;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-line;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
  animation: ${hintIn} ${HINT_MS}ms steps(14, end) both;
  &::after {
    content: "";
    position: absolute;
    right: 26px;
    bottom: -6px;
    border: 6px solid transparent;
    border-bottom: 0;
    border-top-color: #fff4dc;
  }
  ${(p) =>
    p.$pixel &&
    css`
      right: 6px;
      bottom: 72px;
      border-radius: 0;
      font-size: 12px;
      box-shadow: 0 0 0 3px #0b0a12, 4px 4px 0 3px rgba(0, 0, 0, 0.4);
    `}
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const layer = css`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const MoodLayer = styled.div`
  ${layer};
  ${(p) =>
    p.$fade &&
    css`
      animation: ${fadeIn} 1.6s ease both;
    `}
`;

const Glow = styled.div`
  ${layer};
  inset: -20%;
  background: radial-gradient(
    40% 30% at 30% 30%,
    ${(p) => (p.$light ? "rgba(255,255,255,0.5)" : "rgba(255,214,170,0.08)")},
    transparent 70%
  );
  animation: ${drift} 14s ease-in-out infinite alternate;
  ${noMotion};
`;

const Rain = styled.div`
  ${layer};
  opacity: 0.35;
  background-image: repeating-linear-gradient(
      100deg,
      transparent 0 18px,
      rgba(255, 255, 255, 0.18) 18px 19px
    ),
    repeating-linear-gradient(
      100deg,
      transparent 0 31px,
      rgba(255, 255, 255, 0.1) 31px 32px
    );
  background-size: 60px 120px, 90px 200px;
  animation: ${rain} 0.9s linear infinite;
  ${noMotion};
`;

const Vignette = styled.div`
  ${layer};
  background: radial-gradient(120% 90% at 50% 45%, transparent 55%, rgba(0, 0, 0, 0.45) 100%);
`;

const Pulse = styled.div`
  ${layer};
  z-index: 3;
  box-shadow: inset 0 0 90px 30px rgba(170, 20, 30, 0.55);
  animation: ${pulse} 1.6s ease-out both;
  ${noMotion};
`;

const Shaker = styled.div`
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  flex-direction: column;
  ${(p) =>
    p.$shake &&
    css`
      animation: ${shake} 0.5s ease both;
    `}
  ${noMotion};
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 12px 0;
`;

const IconBtn = styled.button`
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  opacity: 0.75;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
`;

const Progress = styled.div`
  flex: 1;
  height: 2px;
  border-radius: 1px;
  background: currentColor;
  opacity: 0.25;
  overflow: hidden;

  span {
    display: block;
    height: 100%;
    background: currentColor;
    transition: width 0.6s ease;
  }
`;

const Opening = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 0 32px 80px;
  text-align: center;
  animation: ${fadeIn} 1.2s ease both;

  p {
    font-size: 17px;
  }
`;

const OpeningTitle = styled.div`
  margin-bottom: 28px;
  font-size: 22px;
  letter-spacing: 0.04em;
`;

const Phones = styled.svg`
  width: 44px;
  height: 44px;
  opacity: 0.85;
  animation: ${float} 3.2s ease-in-out infinite;
  ${noMotion};
`;

const Hint = styled.small`
  margin-top: 18px;
  font-size: 13px;
  opacity: 0.6;
  animation: ${blink} 2.4s ease-in-out infinite;
  ${noMotion};
`;

const Chapter = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 28px 80px;
  text-align: center;
  animation: ${fadeIn} 1.4s ease both;
`;

const ChapterText = styled.h2`
  .pixel & {
    font-size: 19px;
    line-height: 1.6;
    color: #ffcf6b;
    text-shadow: 2px 2px 0 #4a3b6b;
  }
  font-size: 26px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.02em;
  white-space: pre-line;
`;

const Stage = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 0 26px 30px;
  /* '공감' 하트가 있는 구간에서는 오른쪽을 비워 둠 */
  padding-right: ${(p) => (p.$heart ? "80px" : "26px")};
  transition: padding-right 0.4s ease;
  ${(p) =>
    p.$pixel &&
    css`
      /* 픽셀 모드: 게임 대화창 (장면 바로 아래, 높이 고정) */
      flex: 0 0 auto;
      height: clamp(150px, 30vh, 230px);
      margin: 12px 14px auto;
      padding: 12px 16px 24px;
      padding-right: ${p.$heart ? "74px" : "16px"};
      background: rgba(11, 10, 18, 0.94);
      border: 3px solid #fff4dc;
      box-shadow: 0 0 0 3px #0b0a12, inset 0 0 0 2px #2c2547;
      transition: none;
    `}
`;

// 화면이 낮으면 지난 줄부터 위로 잘려나가고, 선택지는 항상 보이게
const Log = styled.div`
  .pixel & {
    padding-top: 0;
    gap: 10px;
    -webkit-mask-image: none;
    mask-image: none;
  }
  min-height: 0;
  flex: 0 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 14px;
  overflow: hidden;
  -webkit-mask-image: linear-gradient(to bottom, transparent 0, #000 22%);
  mask-image: linear-gradient(to bottom, transparent 0, #000 22%);
  padding-top: 40px;
`;

const Line = styled.p`
  margin: 0;
  font-size: 17px;
  line-height: 1.75;
  .pixel & {
    font-size: 14px;
    line-height: 1.8;
    color: #fff4dc;
    font-style: normal;
    animation-timing-function: steps(3, end);
  }
  .pixel &[data-s="speech"] {
    color: #ffcf6b;
  }
  .pixel &[data-s="inner"] {
    color: #7cc6ff;
  }
  word-break: keep-all;
  overflow-wrap: anywhere;
  transition: opacity 0.8s ease;
  animation: ${fadeUp} 0.5s ease both;

  ${(p) =>
    p.$style === "speech" &&
    css`
      color: ${p.$light ? "#7a3e14" : "#ffd6a0"};
    `}
  ${(p) =>
    p.$style === "inner" &&
    css`
      font-size: 15.5px;
      letter-spacing: 0.03em;
      opacity: 0.72;
      padding-left: 12px;
      border-left: 1px solid currentColor;
    `}
`;

const Picked = styled.p`
  align-self: flex-end;
  max-width: 85%;
  margin: 0;
  padding: 8px 14px;
  border-radius: 16px 16px 4px 16px;
  font-size: 14.5px;
  line-height: 1.5;
  background: ${(p) => (p.$light ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.12)")};
  animation: ${fadeUp} 0.4s ease both;
  transition: opacity 0.8s ease;
`;

const Notify = styled.div`
  display: grid;
  grid-template-columns: 34px 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 12px 14px;
  border-radius: 18px;
  font-family: -apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo", "Noto Sans KR", sans-serif;
  color: #1c1c1e;
  background: rgba(245, 245, 247, 0.86);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
  animation: ${slideDown} 0.7s cubic-bezier(0.2, 0.9, 0.3, 1.2) both;
  transition: opacity 0.8s ease;

  strong {
    display: block;
    font-size: 13.5px;
  }
  span {
    display: block;
    font-size: 14px;
    line-height: 1.4;
  }
  small {
    align-self: start;
    font-size: 11px;
    color: #8e8e93;
  }
`;

const NotifyIcon = styled.span`
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border-radius: 9px;
  font-size: 18px;
  color: white;
  background: linear-gradient(#5aa9ff, #2f6fe4);
`;

// 방금 온 알림은 화면 위쪽에 내려와 있다가, 탭하면 독백 기록으로 내려감
const TopNotify = styled.div`
  position: absolute;
  top: 8px;
  left: 14px;
  right: 14px;
  z-index: 4;
`;

const Choices = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 22px;
  cursor: default;
`;

const Prompt = styled.div`
  margin-bottom: 4px;
  font-size: 14px;
  letter-spacing: 0.04em;
  opacity: 0.7;
  animation: ${fadeIn} 0.6s ease both;
`;

const Action = styled.button`
  padding: 14px 16px;
  border: 1px solid ${(p) => (p.$light ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.35)")};
  border-radius: 14px;
  font-family: inherit;
  font-size: 15.5px;
  line-height: 1.5;
  text-align: left;
  color: inherit;
  background: ${(p) => (p.$light ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.18)")};
  cursor: pointer;
  animation: ${fadeUp} 0.5s ease both;
  transition: background 0.2s ease, transform 0.1s ease;

  &:hover {
    background: ${(p) => (p.$light ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.1)")};
  }
  &:active {
    transform: scale(0.98);
  }
`;

const Bubbles = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px 10px;
  padding: 6px 0 4px;
`;

const Bubble = styled.button`
  position: relative;
  max-width: 92%;
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 22px;
  font-family: inherit;
  font-size: 15px;
  line-height: 1.45;
  color: #f6f1ea;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  cursor: pointer;
  animation: ${fadeUp} 0.6s ease both, ${float} 3.6s ease-in-out infinite;
  ${noMotion};

  &::after {
    content: "";
    position: absolute;
    left: 22px;
    bottom: -7px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: inherit;
    border: inherit;
  }
  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
`;

const Next = styled.span`
  .pixel & {
    right: auto;
    left: 50%;
    bottom: 6px;
    animation-timing-function: steps(2, end);
  }
  position: absolute;
  right: 26px;
  bottom: 10px;
  font-size: 10px;
  opacity: 0.5;
  animation: ${blink} 1.4s ease-in-out infinite;
  ${noMotion};
`;

const Blackout = styled.div`
  position: absolute;
  inset: 0;
  z-index: 5;
  display: grid;
  place-items: center;
  background: #000;
  color: #e8e2d8;
  animation: ${fadeIn} 0.7s ease both;
`;

const EndingText = styled.p`
  font-size: 16px;
  letter-spacing: 0.04em;
  animation: ${blink} 2s ease-in-out infinite;
`;

// 개발 서버 전용: 왼쪽 아래, 목록은 위로 펼침
const DevPanel = styled.div`
  position: absolute;
  left: 10px;
  bottom: 8px;
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-start;
  z-index: 6;
  font: 12px sans-serif;
  cursor: default;

  button {
    padding: 4px 8px;
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.5);
    color: white;
    cursor: pointer;
  }
  ul {
    margin-bottom: 6px;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
`;
